import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, formatDate, todayISO } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import StatusBadge from "@/components/StatusBadge";
import ConfirmDialog from "@/components/ConfirmDialog";
import { F } from "@/pages/Employees";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Scissors, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function CashAdvances() {
  const { user } = useAuth();
  const canWrite = ["super_admin", "finance", "direktur"].includes(user.role);
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", project_id: "", tanggal: todayISO(), nominal: 0, cicilan: 1, saldo_awal: false, keterangan: "", metode_pembayaran: "Tunai" });
  const [payFor, setPayFor] = useState(null);
  const [payAmt, setPayAmt] = useState(0);
  const [del, setDel] = useState(null);

  const load = () => api.get("/cash-advances").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/employees").then((r) => setEmployees(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);
  const empName = (id) => employees.find((e) => e.id === id)?.nama || "-";

  const save = async () => {
    if (!form.employee_id || !form.nominal) return toast.error("Pegawai & nominal wajib diisi");
    try { await api.post("/cash-advances", { ...form, nominal: Number(form.nominal), cicilan: Number(form.cicilan) || 1 }); toast.success("Kasbon dibuat"); setOpen(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const doPay = async () => {
    try { await api.post(`/cash-advances/${payFor.id}/payments`, { nominal: Number(payAmt) }); toast.success("Kasbon dipotong"); setPayFor(null); setPayAmt(0); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/cash-advances/${del.id}`); toast.success("Dihapus"); setDel(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const columns = [
    { key: "nomor", label: "No", sortable: true },
    { key: "employee_id", label: "Pegawai", sortable: true, sortValue: (r) => empName(r.employee_id), render: (r) => (
      <div>{empName(r.employee_id)}{r.saldo_awal && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700 align-middle">SALDO AWAL</span>}</div>
    ) },
    { key: "tanggal", label: "Tanggal", render: (r) => formatDate(r.tanggal) },
    { key: "nominal", label: "Nominal", align: "right", render: (r) => rupiah(r.nominal) },
    { key: "cicilan", label: "Cicilan", align: "center", render: (r) => `${r.cicilan || 1}x` },
    { key: "terpotong", label: "Terpotong", align: "right", render: (r) => rupiah(r.terpotong) },
    { key: "sisa", label: "Sisa", align: "right", render: (r) => <span className="font-semibold text-rose-600">{rupiah(r.sisa)}</span> },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    {
      key: "act", label: "Aksi", render: (r) => canWrite && (
        <div className="flex gap-1">
          {r.status !== "Lunas" && <Button size="sm" variant="outline" data-testid={`kasbon-pay-${r.id}`} onClick={() => { setPayFor(r); setPayAmt(r.sisa); }}><Scissors className="h-4 w-4 mr-1" /> Potong</Button>}
          <Button size="icon" variant="ghost" className="text-red-600" data-testid={`kasbon-delete-${r.id}`} onClick={() => setDel(r)}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Kasbon / Pinjaman" subtitle="Kelola kasbon pegawai & pemotongan cicilan">
        {canWrite && <Button className="bg-blue-800 hover:bg-blue-900" data-testid="kasbon-add-btn" onClick={() => { setForm({ employee_id: "", project_id: "", tanggal: todayISO(), nominal: 0, cicilan: 1, saldo_awal: false, keterangan: "", metode_pembayaran: "Tunai" }); setOpen(true); }}><Plus className="h-4 w-4 mr-1" /> Tambah Kasbon</Button>}
      </PageHeader>
      <DataTable columns={columns} data={rows} searchKeys={["nomor"]} testid="kasbon-table" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Tambah Kasbon</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <F label="Pegawai *">
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="kasbon-f-emp"><SelectValue placeholder="Pilih pegawai" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.nama}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Proyek">
              <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Pilih" /></SelectTrigger>
                <SelectContent><SelectItem value="none">-</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Tanggal"><Input type="date" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} /></F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Nominal *"><Input type="number" data-testid="kasbon-f-nominal" value={form.nominal} onChange={(e) => setForm({ ...form, nominal: e.target.value })} /></F>
              <F label="Jumlah Cicilan (x)"><Input type="number" min="1" data-testid="kasbon-f-cicilan" value={form.cicilan} onChange={(e) => setForm({ ...form, cicilan: e.target.value })} /></F>
            </div>
            <label className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 cursor-pointer" data-testid="kasbon-f-saldoawal-wrap">
              <Checkbox checked={form.saldo_awal} onCheckedChange={(v) => setForm({ ...form, saldo_awal: !!v })} data-testid="kasbon-f-saldoawal" />
              <span className="text-sm text-amber-800">Kasbon <b>Saldo Awal</b> (sebelum sistem) — isi nominal = sisa belum terbayar, lewati cek plafon</span>
            </label>
            <F label="Metode"><Input value={form.metode_pembayaran} onChange={(e) => setForm({ ...form, metode_pembayaran: e.target.value })} /></F>
            <F label="Keterangan"><Textarea value={form.keterangan} onChange={(e) => setForm({ ...form, keterangan: e.target.value })} /></F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="bg-blue-800 hover:bg-blue-900" onClick={save} data-testid="kasbon-save-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payFor} onOpenChange={() => setPayFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Potong Kasbon</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Sisa kasbon: <span className="font-mono font-semibold">{rupiah(payFor?.sisa)}</span></p>
            <F label="Nominal Potongan"><Input type="number" data-testid="kasbon-pay-amount" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} /></F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayFor(null)}>Batal</Button>
            <Button className="bg-blue-800 hover:bg-blue-900" onClick={doPay} data-testid="kasbon-pay-confirm">Potong</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!del} onOpenChange={() => setDel(null)} destructive testid="kasbon-delete-dialog"
        title="Hapus Kasbon?" description="Data pemotongan terkait juga akan dihapus." confirmText="Hapus" onConfirm={doDelete} />
    </div>
  );
}
